/**
 * UnblockConfirm — deliberate, confirmed unblock (Phase B, AC-11.1).
 *
 * Unblock does NOT auto-restore the connection — the pair returns to 'none'
 * (AC-11.2). Block/unblock stay on the existing safetyService.
 */

import { useState } from 'react';
import { unblockUser } from '../../services/api/safetyService';
import { useSocialGraph } from '../../context/SocialGraphContext';

interface Props {
  profileId: string;
  displayName?: string;
  onDone?: () => void;
  onCancel?: () => void;
}

export function UnblockConfirm({ profileId, displayName, onDone, onCancel }: Props) {
  const { refresh } = useSocialGraph();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      // safetyService.unblockUser returns { success:false, error } on a server
      // error — it does NOT throw. Inspect the result before treating the
      // unblock as done, or a failed T&S action would false-succeed (AC-11).
      const result = await unblockUser(profileId);
      if (!result.success) {
        setError(result.error ?? 'Failed to unblock');
        return;
      }
      await refresh();
      onDone?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to unblock');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="unblock-confirm" role="alertdialog" aria-modal="true">
      <p>
        Unblock <strong>{displayName ?? 'this person'}</strong>? This does not restore your previous
        connection — you'll both start from scratch.
      </p>
      {error && <p className="unblock-confirm__error">{error}</p>}
      <div className="unblock-confirm__actions">
        <button disabled={busy} onClick={confirm}>
          Unblock
        </button>
        <button disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default UnblockConfirm;
